/**
 * @module useAppBarActions
 * Singleton that lets any page component inject action buttons into the app bar.
 *
 * Pattern: pages call `setActions([...])` on mount and `clearActions()` on
 * unmount (via onBeforeUnmount). The AppBar component renders the `actions`
 * array as buttons in the right side of the bar.
 *
 * Lifecycle note: When Vue Router navigates from page A to page B, the order is:
 *   1. Page B's setup() runs (watchEffect/setActions fires)
 *   2. Page A's onBeforeUnmount fires (clearActions)
 *   3. Page B's onMounted fires
 * This means a naive clearActions would wipe actions that were just set by B.
 * To handle this, clearActions defers to nextTick and checks whether setActions
 * was called in the same tick — if so, the clear is skipped.
 *
 * Example action shape:
 *   { icon: "mdi-export", label: "Export", onClick: () => exportCsv() }
 * A period picker, drawn in the middle of the bar:
 *   { month: "2026-09", onMonth: (ym) => { period.value = ym }, tooltip: "Period" }
 */

import { ref, nextTick } from "vue";

// Shared across all usages — only one page is active at a time
const actions = ref([]);

// What the bar should call the current page. Normally the route's own title is
// right, but a page that switches between sections without navigating (the
// Fusion Eat console) has to say which one is open, the way every other module
// names the page you are on (Maurizio, 2026-09-18).
const pageTitle = ref(null);

// The section within that page, and the colour it is known by — the Fusion Eat
// console colours each report, and the bar should say which one is open in
// that colour while the module's own name keeps the module colour from the
// sidebar (Maurizio, 2026-09-18). Shape: { title, color }.
const pageSection = ref(null);

// The title the app bar is showing right now, resolved by AppBar.vue (pageTitle, else the
// route's own). Every ⋮ bottom sheet heads itself with it (MenuSheetTitle.vue) so the two
// can never disagree (Maurizio, 2026-10-30).
const appBarTitle = ref("");

// Tracks whether setActions was called in the current tick (guards against
// stale clearActions from a departing page wiping the incoming page's actions)
let setCalledThisTick = false;

export function useAppBarActions() {
  /** Override the bar's title; pass null to fall back to the route's own. */
  function setPageTitle(title) {
    pageTitle.value = title || null;
  }

  /** Name the open section beside the page title, in its own colour. */
  function setPageSection(section) {
    pageSection.value = section || null;
  }

  function setActions(newActions) {
    setCalledThisTick = true;
    actions.value = newActions;
  }

  /**
   * Clear all actions. Deferred to nextTick so that if a new page already
   * called setActions during its setup, the clear is skipped.
   */
  function clearActions() {
    nextTick(() => {
      if (!setCalledThisTick) {
        actions.value = [];
        pageTitle.value = null;
        pageSection.value = null;
      }
      setCalledThisTick = false;
    });
  }

  return { actions, setActions, clearActions, pageTitle, setPageTitle, pageSection, setPageSection, appBarTitle };
}
